import React, { useState } from "react";
import { Wrench, Check, Upload } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import api from "@/lib/api";
import { IMG, HANDYMAN_SERVICES, FLYER } from "@/data/site";

const URGENCY = ["Flexible", "This week", "Urgent"];

export default function Handyman() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", task: "", urgency: "Flexible" });
  const [photoName, setPhotoName] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.task) { toast.error("Please add your name, email and describe the job."); return; }
    try {
      await api.post("/enquiries", {
        kind: "handyman", name: form.name, email: form.email, phone: form.phone, postcode: form.postcode,
        message: form.task,
        payload: { urgency: form.urgency, photo: photoName || "none provided" },
      });
      setDone(true);
      toast.success("Request sent to Rossi Ceramics!");
    } catch (err) { toast.error("Could not send. Please try again."); }
  };

  return (
    <div data-testid="handyman-page">
      <Seo title="Handyman Services in Stockport" description="Trusted handyman and home improvement services from Rossi Ceramics — repairs, maintenance, flat-pack, TV mounting, shelves and more across the North West." />
      <PageHero eyebrow="Home improvements" title="Need something fixed?" subtitle="From quick repairs to full home improvements — book a trusted Rossi Ceramics handyman. 24hr call-out service and free estimates." image={IMG.handy[0]} />

      <section className="container-rossi py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <Reveal>
            <h2 className="font-serif text-2xl text-rossi-cream mb-6 flex items-center gap-2"><Wrench size={22} className="text-rossi-gold" /> What we can do</h2>
          </Reveal>
          <div className="grid grid-cols-2 gap-3">
            {HANDYMAN_SERVICES.map((s, i) => (
              <Reveal key={s} delay={(i % 4) * 0.03}>
                <div className="card-lux p-4 text-sm text-rossi-cream/85 flex items-center gap-2"><Check size={15} className="text-rossi-gold shrink-0" /> {s}</div>
              </Reveal>
            ))}
          </div>
          <p className="text-xs text-rossi-stone mt-6">Electrical, gas and structural work is only carried out where legally and appropriately qualified. Emergency availability is confirmed on request.</p>
          <Reveal>
            <img src={FLYER} alt="Rossi Ceramics professional handyman services flyer" className="rounded-2xl mt-8 w-full max-w-md border border-rossi-gold/15 mx-auto lg:mx-0" />
          </Reveal>
        </div>

        <div className="card-lux p-7 h-fit lg:sticky lg:top-24">
          {done ? (
            <div className="py-16 text-center" data-testid="handyman-done"><Check size={40} className="text-rossi-gold mx-auto" /><h3 className="font-serif text-2xl text-rossi-cream mt-4">Request sent</h3><p className="text-sm text-rossi-cream/70 mt-2">Thanks {form.name.split(" ")[0]}, we'll be in touch to arrange your job.</p></div>
          ) : (
            <form onSubmit={submit}>
              <h2 className="font-serif text-2xl text-rossi-cream mb-5">Book a handyman</h2>
              <textarea placeholder="Describe the job (e.g. mount a TV, fix a leaking tap)" rows={3} value={form.task} onChange={(e) => setForm({ ...form, task: e.target.value })} className="w-full bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream mb-3 focus:outline-none focus:border-rossi-gold/50" data-testid="handyman-task" />
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                {[["name", "Full name"], ["email", "Email"], ["phone", "Phone"], ["postcode", "Postcode"]].map(([k, ph]) => (
                  <input key={k} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="bg-rossi-bg border border-white/10 rounded-lg px-3 py-2.5 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50" data-testid={`handyman-${k}`} />
                ))}
              </div>
              <label className="text-sm text-rossi-cream/80 mb-2 block">Urgency</label>
              <div className="flex gap-2 mb-4">
                {URGENCY.map((u) => <button type="button" key={u} onClick={() => setForm({ ...form, urgency: u })} className={`px-4 py-2 rounded-full text-sm ${form.urgency === u ? "bg-rossi-gold text-rossi-bg" : "border border-rossi-gold/30 text-rossi-cream/80"}`} data-testid={`handyman-urgency-${u.toLowerCase().replace(/\s+/g, "-")}`}>{u}</button>)}
              </div>
              <label className="cursor-pointer flex items-center gap-2 text-sm text-rossi-cream/75 mb-5">
                <span className="btn-ghost-gold !py-2 !px-4"><Upload size={15} /> Attach a photo</span>
                <span className="text-rossi-stone text-xs">{photoName || "optional"}</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhotoName(e.target.files?.[0]?.name || "")} data-testid="handyman-photo" />
              </label>
              <button type="submit" className="btn-gold w-full" data-testid="handyman-submit">Request callback</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
